import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {Link} from '@github-ui/react-router'
import {getIconColorClassName, getIconComponent} from '@github-ui/saved-views'
import {CopyIcon, KebabHorizontalIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, RelativeTime} from '@primer/react'
import {memo} from 'react'

import {LABELS} from '../../constants/labels'
import {useDeleteSavedViewDialog} from '../../hooks/use-delete-saved-view-dialog'
import {useDuplicateSavedViewDialog} from '../../hooks/use-duplicate-saved-view-dialog'
import {SAVED_VIEW_OPEN_SOURCES, type SavedViewOpenLocationState} from '../../hooks/use-saved-view-analytics'
import styles from './RepositoryViews.module.css'
import type {SavedView} from './types'

type ViewRowProps = {
  view: SavedView
  owner: string
  repo: string
  canManage: boolean
}

function ViewRowComponent({view, owner, repo, canManage}: ViewRowProps) {
  const viewUrl = `/${owner}/${repo}/issues/views/${view.id}`
  const openFromIndexState: SavedViewOpenLocationState = {savedViewOpenSource: SAVED_VIEW_OPEN_SOURCES.index}

  const {openDeleteDialog, deleteDialog} = useDeleteSavedViewDialog({owner, repo})
  const {openDuplicateDialog, duplicateDialog} = useDuplicateSavedViewDialog({owner, repo})

  return (
    <ListItem
      title={
        <ListItemTitle
          value={view.name}
          href={viewUrl}
          linkProps={{as: Link, to: viewUrl, state: openFromIndexState}}
        />
      }
      role="listitem"
      className={styles.viewRow}
      secondaryActions={
        canManage ? (
          <div className={styles.viewActions}>
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton
                  icon={KebabHorizontalIcon}
                  aria-label={LABELS.views.viewOptionsAriaLabel(view.name)}
                  variant="invisible"
                />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="small">
                <ActionList>
                  <ActionList.Item onSelect={() => openDuplicateDialog(view)}>
                    <ActionList.LeadingVisual>
                      <CopyIcon />
                    </ActionList.LeadingVisual>
                    {LABELS.views.duplicate}
                  </ActionList.Item>
                  <ActionList.Item onSelect={() => openDeleteDialog(view)} variant="danger">
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    {LABELS.views.delete}
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
            {duplicateDialog}
            {deleteDialog}
          </div>
        ) : undefined
      }
    >
      <ListItemLeadingVisual
        className={`${styles.viewLeadingVisual} ${getIconColorClassName(view.color)}`}
        icon={getIconComponent(view.icon)}
      />
      <ListItemMainContent>
        <ListItemDescription>
          <ListItemDescriptionItem>
            {view.creatorLogin ? <span>{LABELS.views.createdBy(view.creatorLogin)} &bull; </span> : null}
            <span>
              {LABELS.views.lastUpdated} <RelativeTime datetime={view.updatedAt} />
            </span>
          </ListItemDescriptionItem>
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

export const ViewRow = memo(ViewRowComponent)
